import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { LoanService } from '../../../services/loan/loan.service';
import { ProductDetailsStore } from './product-details.store';

@Component({
  selector: 'app-product-details',
  imports: [
    CommonModule,
    RouterOutlet,
  ],
  viewProviders: [ProductDetailsStore],
  templateUrl: './product-details.component.html',
  styleUrl: './product-details.component.scss'
})
export class ProductDetailsComponent {
  activeMenu: number = 0;
  _router = inject(Router)
  productId!: string
  router = inject(Router);
  private loanService = inject(LoanService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  protected productDetailsStore = inject(ProductDetailsStore);

  // Use computed signal for product name
  productName = this.productDetailsStore.productName;


  ngOnInit(): void {

    // Read :productId from the route and react to changes (tab switches)
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const id = params.get('productId');
        if (!id) {
          console.error('Missing product id in route params');
          return;
        }
        this.productId = id;
        this.productDetailsStore.setProductId(id);
        this.loanDashboard(id);
      });

    // Set active tab based on current route and keep it in sync on navigation
    this.syncActiveMenuFromRoute();
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncActiveMenuFromRoute());
  }

  private loanDashboard(id: string) {
    // Fetch product by id
    this.loanService.getDashboardData(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (dashboard) => {
          this.productDetailsStore.setData(dashboard);
        },
        error: err => console.error('Failed to load product:', err)
      });
  }

  private syncActiveMenuFromRoute() {
    const child = this.route.firstChild;
    if (!child) {
      this.activeMenu = 0;
      return;
    }

    const segment = child.snapshot.url[0]?.path;

    switch (segment) {
      case 'repayments':
        this.activeMenu = 1;
        break;
      case 'institution':
        this.activeMenu = 2;
        break;
      case 'applicant':
        // Applicant details is logically part of the overview/applications context
        this.activeMenu = 0;
        break;
      case 'overview':
      default:
        this.activeMenu = 0;
        break;
    }
  }

  goBack(): void {
    this._router.navigate(['/products']);
  }

  menuItems: any[] = [
    "Overview",
    "Repayment",
    "Institutions",
  ]

  onMenuChange(index: number) {

    if (!this.productId) {
      console.error('productId is undefined');
      return;
    }

    this.activeMenu = index;

    switch (index) {
      case 0:
        this._router.navigate(['/product-details', this.productId, 'overview']);
        break;
      case 1:
        this._router.navigate(['/product-details', this.productId, 'repayments']);
        break;
      case 2:
        this._router.navigate(['/product-details', this.productId, 'institution']);
        break;
      default:
        this._router.navigate(['/product-details', this.productId, 'overview']);
        break;
    }
  }
}
